import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootReducer } from 'src/redux/rootReducer'
import { HIDE_TOAST } from 'src/redux/toastReducer'
import ToastSuccessSvg from '../other/svg/ToastSuccessSvg'
import ToastCloseSvg from '../other/svg/ToastCloseSvg'

const AUTO_HIDE_MS = 4000
const ACTION_AUTO_HIDE_MS = 6000

const toastShadow =
  '0px 8px 24px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.20), 0px 0px 0px 1px rgba(0,0,0,0.08)'

let navigateFn: ((path: string) => void) | null = null

export const ToastNavigateBridge = (): null => {
  const navigate = useNavigate()

  useEffect(() => {
    navigateFn = navigate
    return () => {
      if (navigateFn === navigate) {
        navigateFn = null
      }
    }
  }, [navigate])

  return null
}

const ToastHost = (): JSX.Element => {
  const dispatch = useDispatch()
  const visible = useSelector((state: RootReducer) => state.toastReducer.visible)
  const message = useSelector((state: RootReducer) => state.toastReducer.message)
  const toastId = useSelector((state: RootReducer) => state.toastReducer.id)
  const actionLabel = useSelector((state: RootReducer) => state.toastReducer.actionLabel)
  const actionPath = useSelector((state: RootReducer) => state.toastReducer.actionPath)

  useEffect(() => {
    if (!visible) {
      return () => {
        window.clearTimeout(0)
      }
    }
    const hideAfter = actionLabel === null ? AUTO_HIDE_MS : ACTION_AUTO_HIDE_MS
    const autoHide = window.setTimeout(() => {
      dispatch(HIDE_TOAST())
    }, hideAfter)
    return () => {
      window.clearTimeout(autoHide)
    }
  }, [visible, toastId, actionLabel, dispatch])

  if (!visible) {
    return <></>
  }

  const handleClose = (): void => {
    dispatch(HIDE_TOAST())
  }

  const handleAction = (): void => {
    if (actionPath === null) {
      return
    }
    dispatch(HIDE_TOAST())
    if (navigateFn !== null) {
      navigateFn(actionPath)
    }
  }

  return createPortal(
    <div
      className="pointer-events-none"
      style={{
        position: 'fixed',
        top: 16,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center'
      }}>
      <div
        className="p-3 rounded-lg inline-flex justify-center items-start gap-2 pointer-events-auto"
        style={{
          backgroundColor: '#FFFFFF',
          boxShadow: toastShadow
        }}
        role="status">
        <div className="size-5 flex justify-center items-center">
          <ToastSuccessSvg className="size-4 text-tag-success-tag-green-icon" />
        </div>
        <div className="inline-flex flex-col justify-start items-start gap-1">
          <div className="justify-start text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5">
            {message}
          </div>
          {actionLabel !== null && actionPath !== null && (
            <button
              type="button"
              onClick={handleAction}
              className="text-foregrounds-fg-interactive text-xs font-semibold font-Montserrat leading-5">
              {actionLabel}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="p-[2.5px] bg-transparent rounded-md flex justify-center items-center overflow-hidden hover:bg-lightGrey"
          aria-label="Close">
          <ToastCloseSvg className="size-3.5 text-icon-secondary" />
        </button>
      </div>
    </div>,
    document.body
  )
}

export default ToastHost
